import { PlusOutlined } from '@ant-design/icons';
import { Button, message } from 'antd';
import { AccessControl } from 'components/AccessControl';
import ClientsSelect from 'components/ClientsSelect';
import { RecordInfoDrawer } from 'components/RecordInfoDrawer';
import { FETCH_ERROR, REQUEST_LOADING } from 'constants/alerts';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useDrawers } from 'hooks/useDrawers';
import { useFilters } from 'hooks/useFilters';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { usePagination } from 'hooks/usePagination';
import { useQueryParam } from 'hooks/useQueryParam';
import React, { useEffect, useMemo, useState } from 'react';
import { ContractorsService } from 'services/ContractorsService';
import { ActionsWrapper } from 'ui/ActionsWrapper';
import { formatDate } from 'utils';
import { ContractorsForm } from './ContractorsForm';
import { ContractorsTable } from './ContractorsTable';

const PAGE_TITLE = 'Контрагенты';

function Contractors() {
  useDocumentTitle(PAGE_TITLE);
  const clientId = useQueryParam('client_id');
  const [loading, setLoading] = useState(false);
  const [contractors, setСontractors] = useState([]);
  const { setCount } = useHeaderChanger(PAGE_TITLE);
  const {
    state: { drawerVisible, formIsActive, activeRecord },
    openDrawer,
    closeDrawer,
    openCreateForm,
    openEditForm,
    closeForm,
  } = useDrawers();
  const {
    page,
    totalRows,
    perPage,
    setTotalRows,
    setPagination,
    resetPagination,
  } = usePagination();
  const { filters, handleFilters, resetFilters } = useFilters({
    client_id: '',
    name: null,
    bin_iin: null,
    address: null,
  });

  const { client_id: selectedClient } = filters;

  const recordFields = useMemo(() => {
    return [
      { label: 'Наименование', key: 'nameRu' },
      { label: 'БИН/ИИН', key: 'bin_iin' },
      { label: 'КОГД', key: 'kogd' },
      { label: 'Адрес', key: 'addressRu' },
      {
        label: 'Статус',
        key: 'is_active',
        computedValue: activeRecord?.is_active ? 'Активный' : 'Не активный',
      },
      {
        label: 'Дата создания',
        key: 'created',
        computedValue: formatDate(activeRecord?.created),
      },
    ];
  }, [activeRecord]);

  useEffect(() => {
    fetchContractors();
  }, [page, perPage, filters]);

  const fetchContractors = async () => {
    if (!page || !perPage) {
      return;
    }

    setLoading(true);

    try {
      const { items, total } = await ContractorsService.getContractors(
        page,
        perPage,
        filters
      );

      setTotalRows(total);
      setСontractors(items);
      setCount(total);
    } catch (err) {
      message.error(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const hide = message.loading(REQUEST_LOADING);
    try {
      const isDeleted = await ContractorsService.deleteContractor(
        activeRecord?.id
      );

      if (isDeleted) {
        closeDrawer();
        fetchContractors();
        message.success('Статус контрагента изменен на НЕ АКТИВЕН');
      }
    } catch (err) {
      message.error('Не удалось изменить статус контрагента');
    } finally {
      hide();
    }
  };

  const handleClientSelect = (id = '') => {
    resetFilters();
    handleFilters({ client_id: id });
    resetPagination();
  };

  return (
    <>
      <ActionsWrapper>
        <ClientsSelect
          pageTitle={PAGE_TITLE}
          clientId={clientId}
          onSelect={handleClientSelect}
          onClear={handleClientSelect}
        />

        <AccessControl pageId="contractors" action="create">
          <Button
            disabled={!selectedClient}
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateForm}
          >
            Создать
          </Button>
        </AccessControl>
      </ActionsWrapper>

      <ContractorsTable
        items={contractors}
        loading={loading}
        totalRows={totalRows}
        current={page}
        pageSize={perPage}
        filters={filters}
        onRowClick={openDrawer}
        onChange={setPagination}
        onFilter={(value) => {
          handleFilters(value);
          resetPagination();
        }}
      />

      <RecordInfoDrawer
        record={activeRecord}
        drawerTitle={activeRecord?.nameRu}
        recordFields={recordFields}
        isVisible={drawerVisible}
        onClose={closeDrawer}
        onEdit={openEditForm}
        onDelete={handleDelete}
        deleteConfirmText="Вы точно хотите поменять статус этого контрагента ?"
        pageId="contractors"
      />

      <ContractorsForm
        isActive={formIsActive}
        initialValues={activeRecord}
        onSuccess={fetchContractors}
        onClose={closeForm}
        clientId={selectedClient}
      />
    </>
  );
}

export { Contractors };
